<script setup lang="ts">
import type { BulletLegendItemInterface } from "@unovis/ts"
import { BulletLegend } from "@unovis/ts"
import { VisBulletLegend } from "@unovis/vue"
import { nextTick, onMounted, ref } from "vue"
import { buttonVariants } from '../button'

const props = withDefaults(defineProps<{ items?: BulletLegendItemInterface[] }>(), {
  items: () => [],
})

const emits = defineEmits<{
  "legendItemClick": [d: BulletLegendItemInterface, i: number]
  "update:items": [payload: BulletLegendItemInterface[]]
}>()

const elRef = ref<HTMLElement>()

/**
 * The Unovis CSS variables this legend overrides.
 *
 * `--vis-legend-label-color` — C-1183, Gate G5 finding S7. Unovis declares
 * `--vis-legend-label-color: #6c778c` on `:root` and only swaps it under
 * `body.theme-dark`, a class no DCS app sets (the portal darkens with `.dark`
 * on `<html>`). So every legend label in every app rendered that ONE hardcoded
 * grey in BOTH themes — measured 3.90:1 light and 2.07:1 dark against `--card`
 * (`.docs/plans/portal-design-reference/census/g5-visual-matrix.md` §5, S7). The
 * light reading fails WCAG AA for the 12px label
 * (`--vis-legend-label-font-size`); the dark one is barely visible at all.
 *
 * Declared HERE rather than in an app stylesheet because this component is the
 * only path to a legend in the library: one declaration and every consumer
 * (portal `HomeRevenuePanel`, `ActiveOwnerHome`, `PartnerHome`, …) picks up its
 * own theme's foreground. `--foreground` clears the 4.5:1 small-text floor with
 * room in both. Re-measured on rendered pixels in the portal (`node
 * cli/contrast-audit.mjs <dev>/dev/design/dashboard --theme both`, which renders
 * a `show-legend` chart): the `legendItemLabel` spans read 17.29:1 light and
 * 8.19:1 dark on `--card`. `--muted-foreground` would also pass but only at
 * 4.62:1 in dark, and these labels are the interactive series toggles, not
 * chrome.
 */
const legendVars = {
  '--vis-legend-bullet-size': '16px',
  '--vis-legend-label-color': 'var(--foreground)',
}

function keepStyling() {
  const selector = `.${BulletLegend.selectors.item}`
  nextTick(() => {
    const elements = elRef.value?.querySelectorAll(selector)
    const classes = buttonVariants({ variant: "ghost", size: "sm" }).split(" ")

    elements?.forEach(el => el.classList.add(...classes, "!inline-flex", "!mr-2"))
  })
}

onMounted(() => {
  keepStyling()
})

function onLegendItemClick(d: BulletLegendItemInterface, i: number) {
  emits("legendItemClick", d, i)
  keepStyling()
}
</script>

<template>
  <div
    ref="elRef" class="max-w-full overflow-hidden" :style="legendVars"
  >
    <VisBulletLegend
      :items="items"
      :on-legend-item-click="onLegendItemClick"
    />
  </div>
</template>
